import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, UserPlus, BarChart3, Save, MessageSquare, Send, Pencil, Search, Upload, FileDown, Loader2, Copy, KeyRound, Trash2, AlertTriangle } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const UsersPage = () => {
  const { token } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [workloads, setWorkloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkload, setEditingWorkload] = useState({});
  const [editingInvigQuota, setEditingInvigQuota] = useState({});
  const [editingContacts, setEditingContacts] = useState({});
  const [channelStatus, setChannelStatus] = useState(null);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({ id: '', name: '', email: '', role: '', job_title_id: '' });
  const [roleChangeConfirm, setRoleChangeConfirm] = useState({ open: false, userId: '', userName: '', currentRole: '', newRole: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkMode, setBulkMode] = useState('merge'); // 'merge' | 'replace'
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, user: null, busy: false });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'teacher',
    job_title_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, jobTitlesRes, workloadsRes, channelsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/job-titles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/workload`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications/channels`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setUsers(usersRes.data);
      setJobTitles(jobTitlesRes.data);
      setWorkloads(workloadsRes.data);
      setChannelStatus(channelsRes.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load users:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Sort alphabetically and filter by search query
  const filteredUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => a.name.localeCompare(b.name));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase();
    return sorted.filter(u =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  // Group users by role
  const roleSections = useMemo(() => {
    const order = [
      { key: 'admin', label: 'Administrators', color: 'bg-slate-800 text-white' },
      { key: 'hod', label: 'Heads of Department (HOD)', color: 'bg-indigo-100 text-indigo-800' },
      { key: 'exco', label: 'EXCO Members', color: 'bg-emerald-100 text-emerald-800' },
      { key: 'teacher', label: 'Teachers', color: 'bg-blue-100 text-blue-800' },
    ];
    return order.map(r => ({
      ...r,
      users: filteredUsers.filter(u => u.role === r.key)
    })).filter(r => r.users.length > 0);
  }, [filteredUsers]);

  const filteredWorkloads = useMemo(() => {
    const sorted = [...workloads].sort((a, b) => a.name.localeCompare(b.name));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase();
    return sorted.filter(w =>
      w.name.toLowerCase().includes(q) || w.email.toLowerCase().includes(q)
    );
  }, [workloads, searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Sanity checks — every field is mandatory; enforce trim + format rules.
    const name = (formData.name || '').trim();
    const email = (formData.email || '').trim().toLowerCase();
    const password = formData.password || '';
    const role = formData.role;

    if (!name) { toast.error('Full name is required'); return; }
    if (name.length < 2) { toast.error('Full name looks too short'); return; }

    // RFC-5322 lite — one local-part, one @, one dot-containing domain, TLD ≥ 2 chars
    const emailRe = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!email) { toast.error('Email is required'); return; }
    if (!emailRe.test(email)) { toast.error('Please enter a valid email address'); return; }

    if (!password) { toast.error('Password is required'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }

    if (!role) { toast.error('Role is required'); return; }

    if (role !== 'admin' && jobTitles.length > 0 && !formData.job_title_id) {
      toast.error('Job title is required');
      return;
    }

    try {
      await axios.post(`${API}/auth/register`, {
        ...formData,
        name,
        email,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/users/${userId}`, { is_active: !currentStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const triggerResetEmail = async (u) => {
    if (!window.confirm(`Send a password reset email to ${u.name} (${u.email})?`)) return;
    try {
      const res = await axios.post(`${API}/users/${u.id}/trigger-reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Reset email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/users/bulk-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'User Upload Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download template');
    }
  };

  const performBulkUpload = async () => {
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      fd.append('mode', bulkMode);
      const res = await axios.post(`${API}/users/bulk-import`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(res.data);
      const deletedMsg = res.data.deleted ? `, ${res.data.deleted} removed` : '';
      toast.success(`Imported ${res.data.created} user(s)${res.data.skipped ? `, ${res.data.skipped} skipped` : ''}${deletedMsg}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) { toast.error('Please choose a file'); return; }
    // Replace mode is destructive — always confirm via AlertDialog first
    if (bulkMode === 'replace') {
      setReplaceConfirmOpen(true);
      return;
    }
    await performBulkUpload();
  };

  const copyCredentialsToClipboard = () => {
    if (!bulkResult?.credentials?.length) return;
    const text = [['Name', 'Email', 'Temp Password'].join('\t'),
      ...bulkResult.credentials.map(c => [c.name, c.email, c.password].join('\t'))
    ].join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Credentials copied to clipboard');
  };

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will clear existing data and create sample accounts. Continue?')) return;
    try {
      const response = await axios.post(`${API}/seed-database`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Database seeded! Created ${response.data.users_created} users and ${response.data.job_titles_created} job titles`);
      fetchData();
    } catch (error) {
      toast.error('Failed to seed database');
    }
  };

  const handleSeedDemoData = async () => {
    if (!window.confirm('This will refresh the Demo accounts (demoadmin / demo1 / demo2) and their timetable, relief, and exam data for the current week. Continue?')) return;
    try {
      const response = await axios.post(`${API}/admin/seed-demo-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const s = response.data.summary || {};
      const parts = Object.entries(s).map(([email, c]) =>
        `${email.split('@')[0]}: ${c.timetable_periods} periods · ${c.relief_duties} relief · ${c.invigilation_duties} invig`
      );
      toast.success(`Demo data refreshed. ${parts.join(' | ')}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh demo data');
    }
  };

  const handleWorkloadSave = async (teacherId) => {
    const val = editingWorkload[teacherId];
    const quotaVal = editingInvigQuota[teacherId];
    const patchBody = {};

    if (val !== undefined) {
      const numVal = val === '' ? null : parseInt(val, 10);
      if (val !== '' && (isNaN(numVal) || numVal < 0)) {
        toast.error('Weekly relief: please enter a valid number or leave blank for default');
        return;
      }
      patchBody.max_weekly_relief = numVal;
    }

    if (quotaVal !== undefined) {
      const q = quotaVal === '' ? null : parseInt(quotaVal, 10);
      if (quotaVal !== '' && (isNaN(q) || q < 0)) {
        toast.error('Invigilation quota: please enter a valid number or leave blank for unlimited');
        return;
      }
      patchBody.max_invigilation_per_season = q;
    }

    if (Object.keys(patchBody).length === 0) {
      return;
    }

    try {
      await axios.patch(`${API}/users/${teacherId}`, patchBody, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Workload limit updated');
      setEditingWorkload(prev => {
        const copy = { ...prev };
        delete copy[teacherId];
        return copy;
      });
      setEditingInvigQuota(prev => {
        const copy = { ...prev };
        delete copy[teacherId];
        return copy;
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to update workload');
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', name: '', role: 'teacher', job_title_id: '' });
  };

  const handleContactSave = async (userId) => {
    const contacts = editingContacts[userId];
    if (!contacts) return;
    try {
      await axios.patch(`${API}/users/${userId}`, contacts, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact details updated');
      setEditingContacts(prev => { const c = { ...prev }; delete c[userId]; return c; });
      fetchData();
    } catch (error) {
      toast.error('Failed to update contacts');
    }
  };

  const handleTestNotification = async (userId, channel) => {
    try {
      const res = await axios.post(`${API}/notifications/test/${userId}?channel=${channel}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.sent || res.data?.success || Object.values(res.data).some(v => v?.success)) {
        toast.success(`Test notification sent via ${channel}`);
      } else {
        toast.error(`Failed: ${res.data?.error || JSON.stringify(res.data)}`);
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const openEditUser = (u) => {
    setEditUserData({
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      job_title_id: u.job_title_id || '',
    });
    setEditUserDialogOpen(true);
  };

  const handleEditUserSave = async () => {
    // Job title is mandatory for non-admin roles when job titles exist
    const needsJobTitle = editUserData.role !== 'admin' && jobTitles.length > 0;
    if (needsJobTitle && !editUserData.job_title_id) {
      toast.error('Job title is required');
      return;
    }
    try {
      await axios.patch(`${API}/users/${editUserData.id}`, {
        name: editUserData.name,
        email: editUserData.email,
        job_title_id: editUserData.job_title_id || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      setEditUserDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirm.user) return;
    setDeleteConfirm(prev => ({ ...prev, busy: true }));
    try {
      const res = await axios.delete(`${API}/users/${deleteConfirm.user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const c = res.data?.cascade || {};
      const cascadeSummary = [
        c.timetables ? `${c.timetables} timetable rows` : null,
        c.future_relief ? `${c.future_relief} future relief` : null,
        c.future_invigilation ? `${c.future_invigilation} future invig.` : null,
        c.future_absences ? `${c.future_absences} future absences` : null,
      ].filter(Boolean).join(', ');
      toast.success(cascadeSummary ? `User deleted (${cascadeSummary})` : 'User deleted');
      setDeleteConfirm({ open: false, user: null, busy: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
      setDeleteConfirm(prev => ({ ...prev, busy: false }));
    }
  };

  const requestRoleChange = (newRole) => {
    if (newRole === editUserData.role) return;
    setRoleChangeConfirm({
      open: true,
      userId: editUserData.id,
      userName: editUserData.name,
      currentRole: editUserData.role,
      newRole
    });
  };

  const confirmRoleChange = async () => {
    try {
      await axios.patch(`${API}/users/${roleChangeConfirm.userId}`, {
        role: roleChangeConfirm.newRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Role changed to ${roleChangeConfirm.newRole}`);
      setRoleChangeConfirm({ open: false, userId: '', userName: '', currentRole: '', newRole: '' });
      setEditUserData(prev => ({ ...prev, role: roleChangeConfirm.newRole }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    }
  };

  return (
    <div data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900">Users</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-2">Manage users, access, and workload limits</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={handleSeedDatabase} variant="outline" className="w-full sm:w-auto" data-testid="seed-database-button">
            <Plus className="mr-2 h-4 w-4" />
            Seed Sample Data
          </Button>
          <Button onClick={handleSeedDemoData} variant="outline" className="w-full sm:w-auto border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="seed-demo-data-button">
            <Plus className="mr-2 h-4 w-4" />
            Refresh Demo Data
          </Button>
          <Button onClick={() => { setBulkDialogOpen(true); setBulkResult(null); setBulkFile(null); }} variant="outline" className="w-full sm:w-auto border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-upload-users-button">
            <Upload className="mr-2 h-4 w-4" />
            Bulk Upload
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 w-full sm:w-auto" data-testid="create-user-button">
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" noValidate>
                <p className="text-[11px] text-slate-500">All fields marked <span className="text-red-500">*</span> are required.</p>
                <div>
                  <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required minLength={2} className="mt-1.5" data-testid="name-input" />
                </div>
                <div>
                  <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-1.5" data-testid="email-input" />
                </div>
                <div>
                  <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                  <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={8} className="mt-1.5" data-testid="password-input" />
                  <p className="text-[11px] text-slate-400 mt-1">At least 8 characters.</p>
                </div>
                <div>
                  <Label>Role <span className="text-red-500">*</span></Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger className="mt-1.5" data-testid="role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teacher">Teacher</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="exco">EXCO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.role !== 'admin' && jobTitles.length > 0 && (
                  <div>
                    <Label>Job Title <span className="text-red-500">*</span></Label>
                    <Select value={formData.job_title_id} onValueChange={(value) => setFormData({ ...formData, job_title_id: value })}>
                      <SelectTrigger className="mt-1.5" data-testid="job-title-select"><SelectValue placeholder="Select job title" /></SelectTrigger>
                      <SelectContent>
                        {jobTitles.map((jt) => <SelectItem key={jt.id} value={jt.id}>{jt.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-button">Cancel</Button>
                  <Button type="submit" className="bg-blue-900 hover:bg-blue-800" data-testid="submit-user-button">Create User</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative mb-4" data-testid="user-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className="pl-9"
          data-testid="user-search-input"
        />
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">All Users</TabsTrigger>
          <TabsTrigger value="messaging" data-testid="tab-messaging">
            <MessageSquare className="mr-1.5 h-4 w-4" />
            Messaging
          </TabsTrigger>
          <TabsTrigger value="workload" data-testid="tab-workload">
            <BarChart3 className="mr-1.5 h-4 w-4" />
            Workload Limits
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          {loading ? (
            <div className="text-slate-600">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-slate-500">{searchQuery ? `No users matching "${searchQuery}"` : 'No users found.'}</p>
            </Card>
          ) : (
            <div className="space-y-6">
              {roleSections.map((section) => (
                <div key={section.key} data-testid={`role-section-${section.key}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">{section.label}</h3>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${section.color}`}>{section.users.length}</span>
                  </div>
                  <div className="space-y-3">
                    {section.users.map((u) => (
                      <Card key={u.id} className="dashboard-card p-4 sm:p-5" data-testid="user-card">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-1">
                              <h3 className="text-base font-semibold text-slate-900">{u.name}</h3>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${section.color}`}>{u.role}</span>
                              <Button variant="ghost" size="sm" onClick={() => openEditUser(u)} className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700" data-testid="edit-user-button">
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => triggerResetEmail(u)} className="h-7 w-7 p-0 text-slate-400 hover:text-blue-700" data-testid="send-reset-email-button" title="Send password reset email">
                                <KeyRound className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ open: true, user: u, busy: false })} className="h-7 w-7 p-0 text-slate-400 hover:text-red-700" data-testid="delete-user-button" title="Delete user permanently">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            <p className="text-sm text-slate-600">{u.email}</p>
                          </div>
                          <div className="flex items-center space-x-4">
                            {u.role !== 'admin' ? (
                              <div className="flex items-center space-x-2">
                                <Label htmlFor={`active-${u.id}`} className="text-sm text-slate-600">{u.is_active ? 'Active' : 'Inactive'}</Label>
                                <Switch id={`active-${u.id}`} checked={u.is_active} onCheckedChange={() => toggleUserStatus(u.id, u.is_active)} data-testid="user-status-toggle" />
                              </div>
                            ) : (
                              <span className="text-sm text-slate-500 px-3 py-2 bg-slate-50 rounded-md">Always Active</span>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Delete User Confirmation */}
          <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !deleteConfirm.busy && setDeleteConfirm({ open, user: open ? deleteConfirm.user : null, busy: false })}>
            <AlertDialogContent data-testid="delete-user-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                  Delete {deleteConfirm.user?.name || 'User'}?
                </AlertDialogTitle>
                <AlertDialogDescription className="font-semibold text-red-800">
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="text-sm text-slate-600 space-y-2">
                <p>The following will be permanently removed:</p>
                <ul className="list-disc pl-5 space-y-0.5 text-slate-600">
                  <li>Account & login credentials ({deleteConfirm.user?.email})</li>
                  <li>All timetable rows they own</li>
                  <li>All <strong>future-dated</strong> relief duties & invigilation assignments</li>
                  <li>All pending future absences</li>
                  <li>Password reset tokens & opt-out requests</li>
                </ul>
                <p className="text-[11px] text-slate-500 pt-1">Past assignments are retained for audit purposes.</p>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-user-cancel-btn" disabled={deleteConfirm.busy}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={confirmDeleteUser} disabled={deleteConfirm.busy} className="bg-red-600 hover:bg-red-700" data-testid="delete-user-confirm-btn">
                  {deleteConfirm.busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                  {deleteConfirm.busy ? 'Deleting...' : 'Yes, delete permanently'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Edit User Dialog */}
          <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="edit-name">Full Name</Label>
                  <Input id="edit-name" value={editUserData.name} onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })} className="mt-1.5" data-testid="edit-name-input" />
                </div>
                <div>
                  <Label htmlFor="edit-email">Email</Label>
                  <Input id="edit-email" type="email" value={editUserData.email} onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })} className="mt-1.5" data-testid="edit-email-input" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={editUserData.role} onValueChange={requestRoleChange}>
                    <SelectTrigger className="mt-1.5" data-testid="edit-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teacher">Teacher</SelectItem>
                      <SelectItem value="hod">HOD</SelectItem>
                      <SelectItem value="exco">EXCO</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editUserData.role !== 'admin' && jobTitles.length > 0 && (
                  <div>
                    <Label>Job Title <span className="text-red-500">*</span></Label>
                    <Select
                      value={editUserData.job_title_id}
                      onValueChange={(value) => setEditUserData({ ...editUserData, job_title_id: value })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="edit-job-title-select">
                        <SelectValue placeholder="Select job title" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobTitles.map((jt) => <SelectItem key={jt.id} value={jt.id}>{jt.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditUserDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleEditUserSave} className="bg-blue-900 hover:bg-blue-800" data-testid="save-edit-user-button">Save Changes</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Role Change Confirmation Dialog */}
          <Dialog open={roleChangeConfirm.open} onOpenChange={(open) => !open && setRoleChangeConfirm(prev => ({ ...prev, open: false }))}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Confirm Role Change</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <p className="text-sm text-slate-700">
                  You are changing the role of <strong>{roleChangeConfirm.userName}</strong> from{' '}
                  <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded">{roleChangeConfirm.currentRole}</span>
                  {' '}to{' '}
                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">{roleChangeConfirm.newRole}</span>
                </p>
                <p className="text-xs text-slate-500">
                  This will change their access permissions. Please confirm.
                </p>
                <div className="flex justify-end space-x-3 pt-2">
                  <Button variant="outline" onClick={() => setRoleChangeConfirm(prev => ({ ...prev, open: false }))} data-testid="cancel-role-change">Cancel</Button>
                  <Button onClick={confirmRoleChange} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-role-change">Confirm Change</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="messaging">
          {/* Channel Status */}
          {channelStatus && (
            <Card className="dashboard-card p-4 sm:p-6 mb-4" data-testid="channel-status-card">
              <h2 className="text-lg font-semibold text-slate-900 mb-3">Notification Channels</h2>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(channelStatus).map(([key, ch]) => (
                  <div key={key} className={`p-3 rounded-lg border text-center ${ch.configured ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                    <p className="text-xs font-semibold text-slate-700">{ch.label}</p>
                    <p className={`text-xs mt-1 font-medium ${ch.configured ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {ch.configured ? 'Connected' : 'Not Configured'}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Per-user contact details */}
          <Card className="dashboard-card p-4 sm:p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-900">User Messaging Contacts</h2>
              <p className="text-sm text-slate-500 mt-1">Configure phone number, Telegram, and WeChat for each user to enable multi-channel notifications.</p>
            </div>
            <div className="space-y-4">
              {filteredUsers.filter(u => u.role !== 'admin').map((u) => {
                const editing = editingContacts[u.id] || {};
                const phone = editing.phone_number !== undefined ? editing.phone_number : (u.phone_number || '');
                const telegram = editing.telegram_chat_id !== undefined ? editing.telegram_chat_id : (u.telegram_chat_id || '');
                const wechat = editing.wechat_openid !== undefined ? editing.wechat_openid : (u.wechat_openid || '');
                const hasChanges = editingContacts[u.id] !== undefined;

                return (
                  <div key={u.id} className="p-4 border border-slate-200 rounded-lg" data-testid="messaging-user-card">
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-sm font-semibold text-slate-900">{u.name}</h3>
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-medium rounded">{u.role}</span>
                      <span className="text-xs text-slate-400">{u.email}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Phone Number (WhatsApp/SMS)</Label>
                        <Input
                          value={phone}
                          onChange={(e) => setEditingContacts(prev => ({
                            ...prev, [u.id]: { ...prev[u.id], phone_number: e.target.value }
                          }))}
                          placeholder="+65 8888 0000"
                          className="mt-1 h-8 text-sm"
                          data-testid="phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Telegram Chat ID</Label>
                        <Input
                          value={telegram}
                          onChange={(e) => setEditingContacts(prev => ({
                            ...prev, [u.id]: { ...prev[u.id], telegram_chat_id: e.target.value }
                          }))}
                          placeholder="123456789"
                          className="mt-1 h-8 text-sm"
                          data-testid="telegram-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">WeChat OpenID</Label>
                        <Input
                          value={wechat}
                          onChange={(e) => setEditingContacts(prev => ({
                            ...prev, [u.id]: { ...prev[u.id], wechat_openid: e.target.value }
                          }))}
                          placeholder="o_xxxxxx"
                          className="mt-1 h-8 text-sm"
                          data-testid="wechat-input"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      {hasChanges && (
                        <Button size="sm" onClick={() => handleContactSave(u.id)} className="bg-blue-900 hover:bg-blue-800 h-7 text-xs" data-testid="save-contacts-button">
                          <Save className="mr-1 h-3 w-3" /> Save
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleTestNotification(u.id, 'all')} className="h-7 text-xs" data-testid="test-all-button">
                        <Send className="mr-1 h-3 w-3" /> Test All Channels
                      </Button>
                      {u.phone_number && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleTestNotification(u.id, 'whatsapp')} className="h-7 text-xs text-green-700" data-testid="test-whatsapp-button">WhatsApp</Button>
                          <Button size="sm" variant="ghost" onClick={() => handleTestNotification(u.id, 'sms')} className="h-7 text-xs text-blue-700" data-testid="test-sms-button">SMS</Button>
                        </>
                      )}
                      {u.telegram_chat_id && (
                        <Button size="sm" variant="ghost" onClick={() => handleTestNotification(u.id, 'telegram')} className="h-7 text-xs text-sky-700" data-testid="test-telegram-button">Telegram</Button>
                      )}
                      {u.wechat_openid && (
                        <Button size="sm" variant="ghost" onClick={() => handleTestNotification(u.id, 'wechat')} className="h-7 text-xs text-emerald-700" data-testid="test-wechat-button">WeChat</Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="workload">
          <Card className="dashboard-card p-4 sm:p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Teacher Workload Limits</h2>
              <p className="text-sm text-slate-500 mt-1">Set per-teacher weekly relief limits. Leave blank to use the default from their job title.</p>
            </div>
            {filteredWorkloads.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">{searchQuery ? `No teachers matching "${searchQuery}"` : 'No teachers found. Seed the database first.'}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="workload-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left p-3 font-medium text-slate-500">Teacher</th>
                      <th className="text-left p-3 font-medium text-slate-500">Job Title</th>
                      <th className="text-center p-3 font-medium text-slate-500">This Week</th>
                      <th className="text-center p-3 font-medium text-slate-500">Default Max</th>
                      <th className="text-center p-3 font-medium text-slate-500">Custom Limit</th>
                      <th className="text-center p-3 font-medium text-slate-500" title="Max invigilation sessions per exam season (blank = unlimited)">Invig. Quota</th>
                      <th className="text-center p-3 font-medium text-slate-500">Status</th>
                      <th className="text-center p-3 font-medium text-slate-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWorkloads.map((w) => {
                      const isEditing = editingWorkload.hasOwnProperty(w.id) || editingInvigQuota.hasOwnProperty(w.id);
                      return (
                        <tr key={w.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="p-3">
                            <p className="font-medium text-slate-900">{w.name}</p>
                            <p className="text-xs text-slate-400">{w.email}</p>
                          </td>
                          <td className="p-3 text-slate-600">{w.job_title}</td>
                          <td className="p-3 text-center font-medium text-slate-900">{w.current_weekly_relief}</td>
                          <td className="p-3 text-center text-slate-500">{w.default_max_from_job_title}</td>
                          <td className="p-3 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="50"
                              className="w-20 mx-auto text-center h-8"
                              placeholder={String(w.default_max_from_job_title)}
                              value={editingWorkload.hasOwnProperty(w.id) ? editingWorkload[w.id] : (w.max_weekly_relief !== null ? w.max_weekly_relief : '')}
                              onChange={(e) => setEditingWorkload(prev => ({ ...prev, [w.id]: e.target.value }))}
                              data-testid={`workload-input-${w.id}`}
                            />
                          </td>
                          <td className="p-3 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              className="w-20 mx-auto text-center h-8"
                              placeholder="∞"
                              value={editingInvigQuota.hasOwnProperty(w.id) ? editingInvigQuota[w.id] : (w.max_invigilation_per_season !== null && w.max_invigilation_per_season !== undefined ? w.max_invigilation_per_season : '')}
                              onChange={(e) => setEditingInvigQuota(prev => ({ ...prev, [w.id]: e.target.value }))}
                              data-testid={`invig-quota-input-${w.id}`}
                              title="Max invigilation sessions per exam season. Blank = unlimited."
                            />
                          </td>
                          <td className="p-3 text-center">
                            {w.at_capacity ? (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">At Capacity</span>
                            ) : w.remaining_capacity <= 3 ? (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">{w.remaining_capacity} left</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">{w.remaining_capacity} left</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {isEditing && (
                              <Button size="sm" variant="outline" className="h-8" onClick={() => handleWorkloadSave(w.id)} data-testid={`save-workload-${w.id}`}>
                                <Save className="h-3.5 w-3.5 mr-1" />
                                Save
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ─── Bulk Upload Users Dialog ─── */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { setBulkDialogOpen(open); if (!open) { setBulkFile(null); setBulkResult(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Upload Users</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
              <p className="font-medium text-slate-700 mb-1">How it works</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Download the template and fill in your staff roster.</li>
                <li>Required columns: <code>name, email, role</code>. Optional: <code>job_title</code>.</li>
                <li>Temporary passwords are <strong>auto-generated</strong> as <code>FirstnameLastname123!</code></li>
                <li>Users <strong>must change their password on first login</strong> (enforced by the app).</li>
                <li>If an email already exists, that user is <strong>never overwritten</strong> — their password, role and settings stay intact.</li>
              </ol>
              <Button type="button" variant="link" size="sm" onClick={handleDownloadTemplate} className="px-0 h-auto mt-2 text-blue-700" data-testid="download-template-btn">
                <FileDown className="mr-1 h-3.5 w-3.5" /> Download Excel template
              </Button>
            </div>

            {/* Upload mode selector */}
            <div>
              <Label className="text-sm font-semibold">Upload Mode</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="bulk-mode-selector">
                {[
                  {
                    value: 'merge',
                    title: 'Retain existing + add new',
                    desc: 'Keeps every current user. Only new emails from the file are added.',
                  },
                  {
                    value: 'replace',
                    title: 'Replace all',
                    desc: 'Deletes users not in the file (matching emails are preserved). Admins are never removed.',
                  },
                ].map(opt => {
                  const active = bulkMode === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setBulkMode(opt.value)}
                      className={`text-left p-3 rounded-md border transition-colors ${active ? (opt.value === 'replace' ? 'bg-red-50 border-red-400 ring-1 ring-red-300' : 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-300') : 'bg-white border-slate-200 hover:border-slate-300'}`}
                      data-testid={`bulk-mode-${opt.value}`}
                    >
                      <div className={`text-sm font-semibold ${active ? (opt.value === 'replace' ? 'text-red-900' : 'text-emerald-900') : 'text-slate-800'}`}>
                        {opt.title}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
              {bulkMode === 'replace' && (
                <p className="text-[11px] text-red-700 mt-1.5" data-testid="replace-warning">
                  ⚠ Destructive. Non-admin users whose email is not in the file (plus their future-dated duties) will be deleted.
                </p>
              )}
            </div>

            <form onSubmit={handleBulkUpload} className="space-y-3">
              <div>
                <Label>Select File (.xlsx or .csv)</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setBulkFile(e.target.files[0])}
                  className="mt-1.5 cursor-pointer"
                  data-testid="bulk-users-file-input"
                />
                {bulkFile && <p className="text-xs text-slate-500 mt-1">Selected: {bulkFile.name}</p>}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>Close</Button>
                <Button type="submit" disabled={!bulkFile || bulkUploading} className="bg-blue-900 hover:bg-blue-800" data-testid="bulk-users-submit">
                  {bulkUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {bulkUploading ? 'Uploading...' : 'Upload & Create'}
                </Button>
              </div>
            </form>

            {bulkResult && (
              <div className="space-y-3 pt-3 border-t border-slate-200" data-testid="bulk-users-result">
                <div className="flex gap-3 text-xs flex-wrap">
                  <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">{bulkResult.created} created</span>
                  {bulkResult.skipped > 0 && <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-medium">{bulkResult.skipped} kept as-is</span>}
                  {bulkResult.deleted > 0 && <span className="px-2 py-1 rounded-full bg-red-100 text-red-800 font-medium">{bulkResult.deleted} removed</span>}
                  <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">{bulkResult.total_rows} total rows</span>
                  <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-medium capitalize">mode: {bulkResult.mode || 'merge'}</span>
                </div>

                {bulkResult.credentials?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <Label className="text-xs font-semibold text-slate-700">Temporary Credentials</Label>
                        <p className="text-[10px] text-amber-700 mt-0.5">⚠ Share securely. Users must change their password on first login.</p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={copyCredentialsToClipboard} className="h-7 text-xs" data-testid="copy-credentials-btn">
                        <Copy className="mr-1 h-3.5 w-3.5" /> Copy all
                      </Button>
                    </div>
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="p-2 text-left">Name</th>
                            <th className="p-2 text-left">Email</th>
                            <th className="p-2 text-left">Temp Password</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkResult.credentials.map((c, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="p-2">{c.name}</td>
                              <td className="p-2 font-mono text-[11px]">{c.email}</td>
                              <td className="p-2 font-mono text-[11px]">{c.password}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {bulkResult.deleted_users?.length > 0 && (
                  <div>
                    <Label className="text-xs font-semibold text-red-700 mb-1.5 block">Removed users ({bulkResult.deleted_users.length})</Label>
                    <div className="max-h-32 overflow-y-auto border border-red-200 rounded bg-red-50 p-2 text-xs">
                      {bulkResult.deleted_users.map((u, i) => (
                        <p key={i}>{u.name || u.email} <span className="font-mono text-[10px] text-red-700/70">({u.email})</span></p>
                      ))}
                    </div>
                  </div>
                )}

                {bulkResult.errors?.length > 0 && (
                  <div>
                    <Label className="text-xs font-semibold text-amber-700 mb-1.5 block">Kept as-is / skipped rows</Label>
                    <div className="max-h-32 overflow-y-auto border border-amber-200 rounded bg-amber-50 p-2 text-xs">
                      {bulkResult.errors.map((e, i) => (
                        <p key={i}>
                          Row {e.row} ({e.email || '—'}): <span className="text-amber-800">{e.reason}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Destructive Replace-mode confirmation */}
      <AlertDialog open={replaceConfirmOpen} onOpenChange={setReplaceConfirmOpen}>
        <AlertDialogContent data-testid="replace-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Replace all users?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-red-800 font-semibold">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="text-sm text-slate-600 space-y-1.5">
            <p>Users whose email is <strong>not</strong> in the uploaded file will be permanently deleted, along with their future-dated duties.</p>
            <p>Preserved: admins and any email that matches a row in the file.</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="replace-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setReplaceConfirmOpen(false); performBulkUpload(); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="replace-confirm-btn"
            >
              Yes, replace users
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
